import { useState } from "react";
import { useListPaiementsUnifies } from "@/lib/backoffice-hooks";
import { formatDateShort } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import type { PaiementUnifie } from "@/lib/api";

function fcfa(n: number) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

// Statuts unifiés exposés par /api/paiements/unifies (cf. PaiementUnifie).
const STATUTS = [
  "",
  "VALIDE",
  "INITIE",
  "EN_COURS",
  "ECHEC",
  "EXPIRE",
  "ANNULE",
  "REJETE",
  "REMBOURSE",
  "EN_ATTENTE",
] as const;
const STATUT_LABEL: Record<string, string> = {
  VALIDE: "Validé",
  INITIE: "Initié",
  EN_COURS: "En cours",
  ECHEC: "Échec",
  EXPIRE: "Expiré",
  ANNULE: "Annulé",
  REJETE: "Rejeté",
  REMBOURSE: "Remboursé",
  EN_ATTENTE: "En attente",
};

const SOURCES = ["", "INTERNE", "MANUEL", "INVITE", "HORS_SYSTEME"] as const;
const SOURCE_LABEL: Record<string, string> = {
  INTERNE: "Interne",
  MANUEL: "Manuel",
  INVITE: "Invité",
  HORS_SYSTEME: "Hors système",
};

// Les paiements invité « pas encore aboutis » qu'on met en évidence.
const FUNNEL_PENDING = new Set(["INITIE", "EN_COURS", "ECHEC", "EXPIRE"]);

/** Couleur du badge de statut unifié (vert = validé ; rouge = échec/rejet/annul ; ambre sinon). */
function statutBadgeClass(statut: string): string {
  if (statut === "VALIDE" || statut === "REMBOURSE") return "bg-emerald-100 text-emerald-700";
  if (statut === "ECHEC" || statut === "REJETE" || statut === "ANNULE" || statut === "EXPIRE")
    return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

/** Surligne une ligne entonnoir invité en attente/échec (réconciliation à faire). */
function isFunnelPending(p: PaiementUnifie): boolean {
  return p.origineLigne === "FUNNEL" && FUNNEL_PENDING.has(p.statutUnifie);
}

/**
 * Section « Réconciliation (vue unifiée) » — partagée par l'espace économe école
 * et l'espace économat. Liste les paiements unifiés (grand-livre + entonnoir
 * invité en cours/échec) d'UN établissement. Le scope (etablissementId) est
 * résolu par le parent et passé en prop.
 */
export default function ReconciliationUnifiee({
  etablissementId,
}: {
  etablissementId: string | undefined;
}) {
  const [statut, setStatut] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const { data, isLoading } = useListPaiementsUnifies(etablissementId, {
    statut: statut || undefined,
    source: source || undefined,
  });
  const rows = data ?? [];
  const funnelPending = rows.filter(isFunnelPending).length;

  return (
    <section className="mt-8" data-testid="reconciliation-unifiee">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Réconciliation</div>
          <h2 className="text-lg font-bold text-navy">Vue unifiée des paiements</h2>
          <p className="text-sm text-slate-600">
            {rows.length} ligne(s)
            {funnelPending > 0 ? ` · ${funnelPending} paiement(s) invité en attente/échec` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          {STATUTS.map((s) => (
            <button
              key={s || "ALL"}
              onClick={() => setStatut(s)}
              className={`text-xs px-2.5 py-1 rounded border ${statut === s ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`unifie-filter-statut-${s || "all"}`}
            >
              {s ? STATUT_LABEL[s] : "Tous"}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 flex flex-wrap gap-1 items-center">
        <span className="text-xs text-slate-500 mr-1">Source :</span>
        {SOURCES.map((s) => (
          <button
            key={s || "ALL"}
            onClick={() => setSource(s)}
            className={`text-xs px-2.5 py-1 rounded border ${source === s ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
            data-testid={`unifie-filter-source-${s || "all"}`}
          >
            {s ? SOURCE_LABEL[s] : "Toutes"}
          </button>
        ))}
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        {!etablissementId && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="unifie-no-scope">
            Sélectionnez un établissement pour afficher la vue unifiée.
          </div>
        )}
        {etablissementId && isLoading && (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin inline text-navy" />
          </div>
        )}
        {etablissementId && !isLoading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Référence</th>
                  <th className="text-left">Origine</th>
                  <th className="text-left">Source</th>
                  <th className="text-left">Payeur</th>
                  <th className="text-right">Montant</th>
                  <th className="text-left pl-3">Mode</th>
                  <th className="text-left">Statut</th>
                  <th className="text-left pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-500">
                      Aucun paiement.
                    </td>
                  </tr>
                )}
                {rows.map((p) => {
                  const highlight = isFunnelPending(p);
                  const payeur = [p.payerPrenoms, p.payerNom].filter(Boolean).join(" ");
                  return (
                    <tr
                      key={p.id}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${highlight ? "bg-amber-50" : ""}`}
                      data-testid={`unifie-row-${p.id}`}
                    >
                      <td className="px-4 py-2.5 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {p.reference || p.numeroPaiement || "·"}
                      </td>
                      <td>
                        <span className={`text-xs px-2 py-0.5 rounded ${p.origineLigne === "FUNNEL" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
                          {p.origineLigne === "FUNNEL" ? "Invité" : "Grand-livre"}
                        </span>
                      </td>
                      <td className="text-xs text-slate-600">{SOURCE_LABEL[p.source] ?? p.source}</td>
                      <td className="text-slate-700">{payeur || "·"}</td>
                      <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montant)}</td>
                      <td className="pl-3 text-xs text-slate-600">{p.mode ? String(p.mode).replace(/_/g, " ") : "·"}</td>
                      <td>
                        <span className={`text-xs px-2 py-0.5 rounded ${statutBadgeClass(p.statutUnifie)}`}>
                          {STATUT_LABEL[p.statutUnifie] ?? p.statutUnifie}
                        </span>
                      </td>
                      <td className="text-xs text-slate-500 pr-4">{formatDateShort(p.paidAt ?? p.datePaiement ?? p.creeLe)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}
